import { Autocomplete, createFilterOptions, FormControl, TextField } from '@mui/material';
import { EMasterPropertyCodes } from 'data/Constants';
import type { ReactElement } from 'react';

import type { IAutocompleteOptionProps, IAutocompleteProps, ILovDetail } from '../../../types';

/**
 * Returns the display label for an autocomplete option based on the property code.
 * @param {string} code - The property code used to determine the label format.
 * @param option - The option object containing `code` and `name` fields.
 * @returns {string} The formatted label string.
 */
const formatOptionLabel = (code: string, option: { code: string; name: string }): string => {
  if (code === EMasterPropertyCodes.RCID) {
    return `${option.code} (${option.name})`;
  } else {
    return `${option.name} (${option.code})`;
  }
};

/**
 * Renders a single option row of the Autocomplete list, highlighting invalid options in red.
 * @param {IAutocompleteOptionProps} props Autocomplete option props
 * @param props.optionProps - Props provided by MUI for the option element, including its `key`.
 * @param {boolean} props.isInvalid - Whether the option is invalid and should be highlighted in red.
 * @param {string} props.label - The text displayed for the option.
 * @returns {JSX.Element} a list item element representing the option.
 */
const AutocompleteOption = ({
  optionProps,
  isInvalid,
  label,
}: IAutocompleteOptionProps): ReactElement => {
  const { key, ...restOptionProps } = optionProps;
  return (
    <li key={key} {...restOptionProps} style={isInvalid ? { color: 'red' } : undefined}>
      {label}
    </li>
  );
};

/**
 * This component renders Autocomplete component to select values
 * @param {IAutocompleteProps} props AutoComplete Props
 * @returns {JSX.Element} a  Autocomplete component.
 */
const AutocompleteInput = (props: IAutocompleteProps): ReactElement => {
  const handleInputChange = (newVal): void => {
    const val = newVal ? JSON.stringify([newVal.code]) : null;
    props.code ? props.handleChange(props.code, val) : props.handleChange(newVal);
  };

  const isValueMismatch = !!props.value?.inValid;

  return (
    <FormControl fullWidth>
      <Autocomplete
        id={props.code ?? 'autocomplete'}
        size={props.size}
        options={props.data as ILovDetail[]}
        onChange={(_, newVal): void => handleInputChange(newVal)}
        getOptionLabel={(option): string => {
          if (props.showCode) {
            return formatOptionLabel(props.code ?? '', option);
          }
          if (props.showSequenceCode) return `${option.name} (${option.sequence})`;
          return option.name;
        }}
        getOptionDisabled={
          props.getOptionDisabled
            ? (option): boolean => props.getOptionDisabled!(option as unknown as ILovDetail)
            : undefined
        }
        renderOption={
          props.getOptionDisabled
            ? (optionProps, option): ReactElement => {
                const lov = option as unknown as ILovDetail;
                const label = props.showCode
                  ? formatOptionLabel(props.code ?? '', option)
                  : (option as { name: string }).name;
                return (
                  <AutocompleteOption
                    optionProps={
                      optionProps as React.HTMLAttributes<HTMLElement> & { key: React.Key }
                    }
                    isInvalid={props.getOptionDisabled!(lov)}
                    label={label}
                  />
                );
              }
            : undefined
        }
        filterOptions={createFilterOptions({ trim: true })} // Create a custom filter that trims whitespace
        disableClearable={props.disableClear}
        value={props?.value as ILovDetail | null}
        disabled={props.disabled}
        data-testid={props.id}
        className={isValueMismatch ? 'autocomplete-value-mismatch' : undefined}
        renderInput={(params): ReactElement => (
          <TextField
            {...params}
            label={props.label}
            error={!!isValueMismatch}
            slotProps={{
              ...params.slotProps,
              inputLabel: {
                ...params.slotProps?.inputLabel,
                className: props.isRequired ? 'required' : undefined,
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};

export default AutocompleteInput;
